import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Camera, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const OPEN_PATHS = ["/terms", "/privacy", "/legal"]; // pages légales toujours lisibles
const MAX_SIZE = 8 * 1024 * 1024;

/** Écran bloquant : un artiste connecté sans photo l'ajoute avant de continuer sur le site. */
export default function PhotoGate() {
    const { user, refresh, logout } = useAuth();
    const { pathname } = useLocation();
    const input = useRef(null);
    const [busy, setBusy] = useState(false);

    if (!user || user.role !== "artist" || (user.avatar_url || "").trim()) return null;
    if (OPEN_PATHS.some((p) => pathname.startsWith(p))) return null;

    const upload = async (file) => {
        if (!file) return;
        if (file.size > MAX_SIZE) { toast.error("Image trop lourde : 8 Mo maximum."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch("/me/profile", { avatar_url: `${BACKEND_URL}/api/files/${data.path}` });
            await refresh();
            toast.success("Photo ajoutée. Bienvenue !");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
            if (input.current) input.current.value = "";
        }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-[#050505]/95 backdrop-blur-sm flex items-center justify-center p-5 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="photo-gate-title" data-testid="photo-gate">
            <div className="w-full max-w-md bg-[#101010] border border-[#262626] rounded-2xl p-6 md:p-8 text-center">
                <span className="mx-auto w-20 h-20 rounded-full bg-[#00d97e]/10 border border-[#00d97e]/40 flex items-center justify-center">
                    <Camera className="w-8 h-8 text-[#00d97e]" />
                </span>
                <h2 id="photo-gate-title" className="mt-5 font-display text-2xl font-semibold tracking-tight">Ajoute ta photo pour continuer</h2>
                <p className="mt-3 text-sm text-[#A8A8A8] leading-relaxed">Elle est obligatoire pour faire valider ton profil. Choisis une photo nette de toi ou de ton logo d&apos;artiste.</p>
                <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="photo-gate-input" />
                <button type="button" disabled={busy} onClick={() => input.current?.click()} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#00d97e] hover:bg-[#00f099] disabled:opacity-60 text-[#0F0F0F] font-bold py-3 rounded-full" data-testid="photo-gate-button">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi en cours…</> : "Choisir ma photo"}
                </button>
                <p className="mt-3 text-xs text-[#6E6E6E]">JPG, PNG ou WebP · 8 Mo maximum</p>
                <button type="button" onClick={logout} className="mt-5 inline-flex items-center gap-1.5 text-xs text-[#A8A8A8] hover:text-[#F4F4F4]" data-testid="photo-gate-logout">
                    <LogOut className="w-3.5 h-3.5" /> Me déconnecter
                </button>
            </div>
        </div>
    );
}
